/**
 * iTow design system — MUI 5 theme tokens.
 *
 * Palette mirrors the Angular theme.scss values exactly so both SPAs look
 * identical during the per-role switchover wave (see SPEC.md §Rollout).
 *
 * CSS custom properties are exposed on :root so non-MUI elements (e.g. a
 * Leaflet map control) can access the same values without importing this module.
 */
import {
  createTheme,
  type Theme,
  type PaletteOptions,
} from '@mui/material/styles';
import { createContext, useContext } from 'react';

// ─── Design tokens ───────────────────────────────────────────────────────────

export const ITOW_COLORS = {
  primary: '#002444',
  secondary: '#084569',
  accent: '#9c283d',   // maps to MUI error in light, reused as accent elsewhere
  success: '#4A9751',
  error: '#C5422D',
  warning: '#b45309',
} as const;

/**
 * Ordinal ramp for time-ordered chart marks — one hue in the brand's steel-blue
 * family, seven monotone lightness steps, oldest bucket → newest.
 *
 * Ordinal rather than categorical on purpose. Days of the week are a *sequence*:
 * reordering them changes the meaning, so the ramp lets the reader see the
 * direction of time in the color. What it must NOT be is a value-ramp — colouring
 * each bar darker-where-taller would re-encode what bar height already shows and
 * spend the only free channel on nothing. Index by position in the window, never
 * by magnitude.
 *
 * Multi-hue is also out: a rainbow across one series invents seven identities
 * where there is one.
 *
 * Both arrays run light → dark and are validated as ordinal ramps (monotone
 * lightness, every adjacent gap ΔL ≥ 0.06, single hue within a few degrees, and
 * the lowest-contrast end clearing 2:1 against that mode's surface):
 *   light on #ffffff — light end 2.19:1
 *   dark  on #101f2d — low end   2.49:1
 * Dark mode applies its ramp reversed, so the newest bucket is the brightest mark
 * against a dark surface rather than the murkiest.
 */
export const ITOW_CHART_RAMP = {
  light: ['#8fb4d0', '#729ec1', '#5688b0', '#3d729a', '#2a5b81', '#1a4466', '#0b2f4b'],
  dark:  ['#dcefff', '#b8d9f2', '#95c2e4', '#74abd5', '#5793c1', '#3e7aa6', '#2a6089'],
} as const;

/**
 * Categorical series colours — two identities, in fixed order.
 *
 * Distinct from ITOW_CHART_RAMP, which is ordinal: a ramp encodes *sequence*
 * and cannot carry two identities, because reading "towing" off a lighter blue
 * than "roadside" is reading position in a sequence that does not exist. Two
 * service lines are a categorical encoding and need two hues.
 *
 * Assigned by entity and never by rank: towing is always the blue, roadside
 * always the amber, whatever the filter leaves standing. A palette that
 * repaints the survivors when a series drops out makes every chart on the page
 * mean something different from the last time you looked at it.
 *
 * Both pairs were run through the six-check validator rather than eyeballed,
 * against that mode's paper surface:
 *   light on #ffffff — CVD ΔE 23.6 (protan) / 26.7 (tritan), normal 28.8, all
 *                      inside the L 0.43–0.77 band, chroma and contrast pass
 *   dark  on #101f2d — CVD ΔE 22.1 / 24.5, normal 24.8, inside L 0.48–0.67
 * Dark is a separate selection, not an automatic lightening: the light pair
 * sits above the dark band and would read as two pastels on navy.
 *
 * Status colours (success/warning/error) are deliberately NOT reused here. They
 * are reserved for state, and a reader who has learned that amber means
 * "attention" should not meet it again as the name of a service line.
 */
export const ITOW_CHART_SERIES = {
  light: { towing: '#1668b8', roadside: '#b45309' },
  dark:  { towing: '#4a93d1', roadside: '#c8802e' },
} as const;

/**
 * Surface conventions shared by both modes (redesign, mockup `04-admin-portal`).
 *
 * The mockups render every card/panel as a rounded, hairline-bordered surface
 * carrying a soft two-part shadow — a tight contact shadow plus a wide diffuse
 * one. MUI's default `elevation` ramp is a different (Material 2) curve, so the
 * redesign standardises on `elevation={0}` + these values instead.
 */
export const ITOW_SURFACE = {
  /** Card / panel / KPI radius — `13px` in the mockups. */
  radius: 13,
  /** Control radius (buttons, inputs, filter fields) — `9–10px` in the mockups. */
  controlRadius: 10,
  /** Rail nav-item radius. */
  navRadius: 9,
} as const;

/**
 * Navy sidebar rail (mockup `04-admin-portal`).
 *
 * The rail is navy in BOTH color modes, so its foreground values can't come
 * from the mode-dependent palette — they live here as their own small scale.
 * Feature code must reference these rather than literal hex (SPEC.md).
 */
export const ITOW_RAIL = {
  bg: ITOW_COLORS.primary,
  /** Default nav-item label color. */
  ink: '#cdd9e6',
  /** Sub-item / secondary label color. */
  muted: '#7f96ac',
  /** Active sub-item dot + active parent icon. */
  marker: '#4d84ab',
  /**
   * Left accent bar on the active row.
   *
   * The wash alone was doing all the work of marking "you are here" and it
   * could not carry it: 11% white on navy is close to invisible at a glance,
   * so the rail read as a flat list with no current page. A saturated bar is
   * the cheap, unambiguous signal — and it reads without relying on colour
   * contrast alone, which the wash did.
   */
  activeAccent: '#5fa8d8',
  /** Section eyebrow ("OPERATIONS" / "SETUP") above a group of nav rows. */
  eyebrow: '#6f8aa4',
  /** Active item background wash. Brighter than the old .11 — see activeAccent. */
  activeBg: 'rgba(255,255,255,.16)',
  /** Active sub-item background wash (a touch lighter than the parent's). */
  activeSubBg: 'rgba(255,255,255,.12)',
  /** Hover wash for inactive items. */
  hoverBg: 'rgba(255,255,255,.07)',
  /** Hairline used for the sub-nav guide rail and the user-block divider. */
  hairline: 'rgba(255,255,255,.1)',
  divider: 'rgba(255,255,255,.08)',
} as const;

const SHADOW_LIGHT =
  '0 2px 4px rgba(9,30,52,.05), 0 12px 30px -16px rgba(9,30,52,.2)';
const SHADOW_DARK =
  '0 2px 4px rgba(0,0,0,.3), 0 12px 30px -16px rgba(0,0,0,.55)';

// ─── Shared palette shape (fields that are the same in both modes) ────────────

const sharedPalette = {
  primary: {
    main: ITOW_COLORS.primary,
    contrastText: '#ffffff',
  },
  secondary: {
    main: ITOW_COLORS.secondary,
    contrastText: '#ffffff',
  },
  success: {
    main: ITOW_COLORS.success,
    contrastText: '#ffffff',
  },
  error: {
    main: ITOW_COLORS.error,
    contrastText: '#ffffff',
  },
  warning: {
    main: ITOW_COLORS.warning,
    contrastText: '#ffffff',
  },
  // `accent` is not a standard MUI slot; surfaces that need #9c283d use
  // `theme.palette.error.main` for destructive actions per SPEC.md.
} satisfies Partial<PaletteOptions>;

// ─── Shared typography / component overrides ─────────────────────────────────

function buildComponents(mode: ColorMode): Theme['components'] {
  const surfaceShadow = mode === 'light' ? SHADOW_LIGHT : SHADOW_DARK;

  return {
    MuiCssBaseline: {
      styleOverrides: `
      :root {
        --itow-primary:   ${ITOW_COLORS.primary};
        --itow-secondary: ${ITOW_COLORS.secondary};
        --itow-accent:    ${ITOW_COLORS.accent};
        --itow-success:   ${ITOW_COLORS.success};
        --itow-error:     ${ITOW_COLORS.error};
        --itow-warning:   ${ITOW_COLORS.warning};
        --itow-surface-shadow: ${surfaceShadow};
        --itow-surface-radius: ${ITOW_SURFACE.radius}px;
      }
    `,
    },

    // ── Surfaces ─────────────────────────────────────────────────────────
    // Paper/Card default to the rounded + hairline + soft-shadow treatment
    // from the mockups. `variant="outlined"` still opts out of the shadow,
    // and an explicit `elevation` prop above 0 still wins (MUI applies its
    // own boxShadow last), so escape hatches remain.
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        rounded: { borderRadius: ITOW_SURFACE.radius },
        elevation0: ({ theme }) => ({
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: surfaceShadow,
        }),
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { borderRadius: ITOW_SURFACE.radius },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: ITOW_SURFACE.radius },
      },
    },
    // Menus/popovers float above the page; the card border reads as a seam there.
    MuiMenu: {
      styleOverrides: {
        paper: { borderRadius: ITOW_SURFACE.controlRadius },
      },
    },

    // ── Controls ─────────────────────────────────────────────────────────
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: ITOW_SURFACE.controlRadius,
          textTransform: 'none',
          fontWeight: 700,
        },
        sizeMedium: { minHeight: 38 },
        // 44px floor for touch targets (SPEC.md accessibility rule).
        sizeLarge: { minHeight: 44 },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: ITOW_SURFACE.navRadius },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: ITOW_SURFACE.controlRadius,
          // MUI's resting outline is rgba(255,255,255,.23), which nearly
          // disappears against the dark canvas. Lift it so a field still reads
          // as a field before you interact with it.
          ...(mode === 'dark' && {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255,255,255,.32)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255,255,255,.48)',
            },
          }),
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
        }),
      },
    },

    // ── Field labels ─────────────────────────────────────────────────────
    // Once a field has a value the label shrinks to 0.75 scale — an effective
    // 12px. At that size MUI's default `text.secondary` reads as decoration
    // rather than as the field's name, even though it clears AAA on contrast
    // (7.5:1 in dark). Contrast isn't the problem; size is. So the shrunk
    // label takes full-strength ink and a heavier weight.
    //
    // The resting (unshrunk) label stays muted on purpose: there it sits at
    // full size doing a placeholder's job, and brightening it would make an
    // empty field look filled.
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.MuiInputLabel-shrink': {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          '&.MuiInputLabel-shrink.Mui-focused': { color: theme.palette.primary.main },
          '&.MuiInputLabel-shrink.Mui-error': { color: theme.palette.error.main },
          '&.MuiInputLabel-shrink.Mui-disabled': { color: theme.palette.text.disabled },
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: { borderRadius: ITOW_SURFACE.controlRadius, textTransform: 'none' },
      },
    },

    // ── Status chips ─────────────────────────────────────────────────────
    // Mockup chips are uppercase pills with a leading dot; `<StatusChip>`
    // supplies the per-status color and the dot.
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 800,
          fontSize: '0.625rem',
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        },
        label: { paddingLeft: 9, paddingRight: 9 },
      },
    },

    // ── Data tables ──────────────────────────────────────────────────────
    MuiTableCell: {
      styleOverrides: {
        head: ({ theme }) => ({
          fontSize: '0.625rem',
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
          fontWeight: 700,
          color: theme.palette.text.secondary,
          backgroundColor:
            theme.palette.mode === 'light' ? '#f6f9fc' : 'rgba(255,255,255,.03)',
        }),
      },
    },

    MuiAppBar: {
      defaultProps: { elevation: 0 },
    },
    MuiTooltip: {
      defaultProps: {
        // Slight enter delay so casual mouse passes don't pop tooltips constantly.
        // Keyboard focus still surfaces them immediately (MUI splits these).
        enterDelay: 400,
        enterNextDelay: 200,
      },
    },
  };
}

// ─── Typography scale (6 steps; removes Angular's hard-coded pt/px values) ───

const typography = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  h1: { fontSize: '2.5rem',  fontWeight: 700, lineHeight: 1.2 },
  h2: { fontSize: '2rem',    fontWeight: 700, lineHeight: 1.3 },
  h3: { fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.35 },
  h4: { fontSize: '1.5rem',  fontWeight: 600, lineHeight: 1.4 },
  h5: { fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.45 },
  h6: { fontSize: '1rem',    fontWeight: 500, lineHeight: 1.5 },
  body1: { fontSize: '1rem',    lineHeight: 1.6 },
  body2: { fontSize: '0.875rem', lineHeight: 1.57 },
  caption: { fontSize: '0.75rem' },
  overline: { fontSize: '0.6875rem', letterSpacing: '0.08em' },
} as const;

// ─── Light theme ─────────────────────────────────────────────────────────────

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    ...sharedPalette,
    background: {
      default: '#eef2f7',
      paper: '#ffffff',
    },
    text: {
      primary: '#16222f',
      secondary: '#5c6a7a',
    },
    divider: '#e2e8f0',
  },
  shape: { borderRadius: ITOW_SURFACE.controlRadius },
  typography,
  components: buildComponents('light'),
});

// ─── Dark theme ──────────────────────────────────────────────────────────────

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    ...sharedPalette,
    // In dark mode lighten the primary slightly so it meets WCAG AA on dark bg
    primary: {
      main: '#4d8ab5',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0a1420',
      paper: '#101f2d',
    },
    text: {
      primary: '#e7eef6',
      secondary: '#9fb0c1',
    },
    divider: '#21384b',
  },
  shape: { borderRadius: ITOW_SURFACE.controlRadius },
  typography,
  components: buildComponents('dark'),
});

// ─── Color mode context + hook ────────────────────────────────────────────────

export type ColorMode = 'light' | 'dark';

export interface ColorModeContextValue {
  mode: ColorMode;
  toggleColorMode: () => void;
}

/**
 * ColorModeContext is consumed by any component that needs the current mode or
 * needs to trigger a toggle (e.g. the AppBar theme-switch button).
 *
 * The provider lives in main.tsx so the entire app tree is covered.
 */
export const ColorModeContext = createContext<ColorModeContextValue>({
  mode: 'light',
  // Default no-op; replaced by real toggle once the provider mounts.
  toggleColorMode: () => undefined,
});

export function useColorMode(): ColorModeContextValue {
  return useContext(ColorModeContext);
}
